/**
 * ActiveBatchPanel — replaces the document tree in the sidebar when the
 * user is in the ingest pipeline.
 *
 * Why: the workspace sidebar shows COMMITTED documents, but ingest items
 * aren't documents until the user commits them. New users land on /ingest
 * with 5 items in flight and feel "lost" — the sidebar provides no
 * orientation back to the work they just kicked off.
 *
 * What it shows:
 *   - "Active batch" header with truncated batch ID
 *   - Per-item rows: filename + status pill, click → item preview
 *   - Quick-link to "Review queue" (full review page)
 *   - Quick-link to "Start new batch" (upload page)
 *
 * Source of truth for which batch is "active":
 *   1. /ingest/batches/:batchId/* — URL param wins
 *   2. otherwise look up `agentwiki-active-batch` from localStorage
 *      (set by UploadPage so a fresh /ingest/upload visit can resume)
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, FileText, Layers } from 'lucide-react'
import { ingestApiClient } from '../../features/ingest/ingest-api-client'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'

type RawStatus = string

interface RawBatchItem {
  id: string
  uploadId?: string
  filename?: string
  status: RawStatus
}

function normalizeStatus(s: RawStatus): { label: string; tone: 'queued' | 'processing' | 'ready' | 'failed' } {
  if (s === 'ready') return { label: 'Ready', tone: 'ready' }
  if (s === 'queued') return { label: 'Queued', tone: 'queued' }
  if (s === 'extracting' || s === 'ocr-retry') return { label: 'Processing', tone: 'processing' }
  if (s.startsWith('failed') || s === 'rejected') return { label: 'Failed', tone: 'failed' }
  if (s === 'accepted') return { label: 'Accepted', tone: 'ready' }
  return { label: s, tone: 'processing' }
}

function useActiveBatchId(): string | null {
  // URL param has priority — user is actively viewing this batch.
  const { batchId: urlBatchId } = useParams<{ batchId?: string }>()
  const [storedId, setStoredId] = useState<string | null>(null)

  useEffect(() => {
    if (urlBatchId) return
    const raw = window.localStorage.getItem('agentwiki-active-batch')
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as { batchId?: string }
      if (parsed.batchId) setStoredId(parsed.batchId)
    } catch {
      // ignore
    }
  }, [urlBatchId])

  return urlBatchId ?? storedId
}

function useActiveItemId(): string | null {
  const { itemId } = useParams<{ itemId?: string }>()
  return itemId ?? null
}

export function ActiveBatchPanel() {
  const navigate = useNavigate()
  const isDark = useAppStore((s) => s.theme === 'dark')
  const batchId = useActiveBatchId()
  const activeItemId = useActiveItemId()

  const { data, isLoading } = useQuery({
    queryKey: ['sidebar', 'batchItems', batchId],
    queryFn: () => ingestApiClient.getBatchItems(batchId!, { pageSize: 50 }),
    enabled: Boolean(batchId),
    // Poll only while at least one item is still in flight. Once everything
    // is terminal (ready/failed/accepted/rejected), stop — repeated polling
    // was causing visible main-thread jank during PDF rendering.
    refetchInterval: (query) => {
      const items = (query.state.data?.items ?? []) as Array<{ status?: string }>
      if (items.length === 0) return 6000
      const allTerminal = items.every((it) => {
        const s = it.status ?? ''
        return s === 'ready' || s === 'accepted' || s === 'rejected' || s.startsWith('failed')
      })
      return allTerminal ? false : 6000
    },
    staleTime: 2000,
  })

  if (!batchId) {
    return (
      <div className="px-3 py-4 space-y-2">
        <p className={cn('text-xs', isDark ? 'text-neutral-400' : 'text-neutral-600')}>
          No active batch.
        </p>
        <button
          type="button"
          onClick={() => navigate('/ingest/upload')}
          className={cn(
            'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium',
            isDark ? 'text-brand-300 hover:bg-surface-3' : 'text-brand-600 hover:bg-neutral-100',
          )}
        >
          Start new batch <ArrowRight size={12} />
        </button>
      </div>
    )
  }

  const items = (data?.items ?? []) as unknown as RawBatchItem[]
  const counts = items.reduce(
    (acc, it) => {
      const tone = normalizeStatus(it.status).tone
      acc[tone] = (acc[tone] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )
  const totalReady = (counts.ready ?? 0)
  const totalProcessing = (counts.processing ?? 0) + (counts.queued ?? 0)
  const totalFailed = counts.failed ?? 0

  return (
    <div className="space-y-1.5">
      {/* Header */}
      <div className={cn('px-3 pt-2 pb-1.5 border-b', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
        <div className="flex items-center justify-between gap-2">
          <span className={cn('text-[10px] font-semibold uppercase tracking-wider', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
            Active batch
          </span>
          <span className={cn('font-mono text-[10px]', isDark ? 'text-neutral-500' : 'text-neutral-400')} title={batchId}>
            #{batchId.slice(0, 6)}…
          </span>
        </div>
        {/* Aggregate counters */}
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {totalReady > 0 && (
            <span className={cn('inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]', isDark ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-100 text-emerald-700')}>
              {totalReady} ready
            </span>
          )}
          {totalProcessing > 0 && (
            <span className={cn('inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]', isDark ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-100 text-amber-800')}>
              {totalProcessing} processing
            </span>
          )}
          {totalFailed > 0 && (
            <span className={cn('inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]', isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-100 text-red-700')}>
              {totalFailed} failed
            </span>
          )}
          {items.length === 0 && !isLoading && (
            <span className={cn('text-[10px]', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
              empty
            </span>
          )}
        </div>
      </div>

      {/* Item list */}
      <ul className="px-1 space-y-0.5 max-h-[40vh] overflow-y-auto">
        {isLoading && items.length === 0 && (
          Array.from({ length: 3 }).map((_, i) => (
            <li
              key={i}
              className={cn(
                'h-7 mx-1 my-0.5 rounded animate-pulse',
                isDark ? 'bg-surface-3' : 'bg-neutral-100',
              )}
            />
          ))
        )}
        {items.map((item) => {
          const norm = normalizeStatus(item.status)
          const toneClass =
            norm.tone === 'ready'
              ? isDark ? 'bg-emerald-500' : 'bg-emerald-500'
              : norm.tone === 'failed'
                ? isDark ? 'bg-red-500' : 'bg-red-500'
                : norm.tone === 'processing'
                  ? isDark ? 'bg-amber-400' : 'bg-amber-500'
                  : isDark ? 'bg-neutral-500' : 'bg-neutral-400'
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => navigate(`/ingest/batches/${batchId}/items/${item.id}`)}
                aria-current={activeItemId === item.id ? 'page' : undefined}
                className={cn(
                  'group flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs',
                  activeItemId === item.id
                    ? isDark
                      ? 'bg-brand-500/10 text-brand-200 ring-1 ring-inset ring-brand-500/30'
                      : 'bg-brand-500/10 text-brand-700 ring-1 ring-inset ring-brand-500/30'
                    : isDark
                      ? 'text-neutral-300 hover:bg-surface-3 hover:text-neutral-100'
                      : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900',
                )}
                title={`${item.filename ?? '(no filename)'} — ${norm.label}`}
              >
                <FileText size={12} aria-hidden="true" className="shrink-0 opacity-60" />
                <span className="flex-1 truncate">{item.filename ?? '(unnamed)'}</span>
                <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', toneClass)} aria-label={norm.label} />
              </button>
            </li>
          )
        })}
      </ul>

      {/* Footer actions */}
      <div className={cn('mt-1 px-2 pt-2 pb-1 border-t space-y-0.5', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
        <button
          type="button"
          onClick={() => navigate(`/ingest/batches/${batchId}/review`)}
          className={cn(
            'flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs',
            isDark ? 'text-neutral-300 hover:bg-surface-3' : 'text-neutral-700 hover:bg-neutral-100',
          )}
        >
          <Layers size={12} aria-hidden="true" />
          Review queue
          <ArrowRight size={12} aria-hidden="true" className="ml-auto opacity-50" />
        </button>
        <button
          type="button"
          onClick={() => navigate('/ingest/upload')}
          className={cn(
            'flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs',
            isDark ? 'text-neutral-400 hover:bg-surface-3' : 'text-neutral-600 hover:bg-neutral-100',
          )}
        >
          + Start new batch
        </button>
      </div>
    </div>
  )
}
